import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import JewelleryFilterBar from '../components/JewelleryFilterBar';
import HelpMeChooseWizard from '../components/HelpMeChooseWizard';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { LiveRateTicker } from '../components/LiveRateTicker';
import ProductCard from '../components/ProductCard';
import './JewelleryListPage.css';
import ImageCropper from '../components/ImageCropper';

const initialEditState = { name: '', type: '', price: '', weight: '', description: '', image: '', purity: '', huid: '', status: '', visibility: false };
const initialAddState = { name: '', type: '', price: '', weight: '', description: '', image: '', purity: '', huid: '', status: 'Draft', visibility: false };

const JewelleryListPage = ({ isAdmin }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Determine API Base URL for direct access (bypass proxy for uploads)
  const isProduction = process.env.NODE_ENV === 'production';
  const API_BASE_URL = isProduction ? '' : 'http://localhost:5050';

  const [items, setItems] = useState([]);

  const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  // Image Helper: Validate aspect ratio and size
  const validateImage = (file) => {
    setImageWarning('');
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      const width = img.width;
      const height = img.height;
      URL.revokeObjectURL(objectUrl);

      let warnings = [];
      // 1. Check Aspect Ratio (Allow small tolerance)
      const ratio = width / height;
      if (ratio < 0.9 || ratio > 1.1) {
        warnings.push("⚠️ Aspect ratio is not 1:1 (Square). Product cards may look inconsistent.");
      }
      // 2. Check Resolution (Recommend 2000px, Warn if < 800px)
      if (width < 800 || height < 800) {
        warnings.push("⚠️ Low resolution. Recommended: 2000x2000px for best quality.");
      }
      // 3. Check Naming Convention (SEO) - REMOVED per user request
      // if (!file.name.toLowerCase().startsWith('mab-')) {
      //   warnings.push("⚠️ Filename should start with 'mab-' (e.g. mab-gold-ring-rajasthan.jpg) for SEO.");
      // }

      if (warnings.length > 0) {
        setImageWarning(warnings.join(' '));
      }
    };
    img.src = objectUrl;
  };
  const [filters, setFilters] = useState({ name: '', type: '', minPrice: '', maxPrice: '', minWeight: '', maxWeight: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(initialEditState);
  const [showDeleteId, setShowDeleteId] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState(initialAddState);
  const [detailItem, setDetailItem] = useState(null);
  const [enquireOpen, setEnquireOpen] = useState(false);
  const [enquireForm, setEnquireForm] = useState({ name: '', email: '', message: '' });
  const [enquireSuccess, setEnquireSuccess] = useState(false);
  const [imageWarning, setImageWarning] = useState('');
  const [showCropper, setShowCropper] = useState(false);
  const [tempImgSrc, setTempImgSrc] = useState(null);
  const [pendingFile, setPendingFile] = useState(null); // Keep track of original filename // Image standard validation warning

  // New Features State
  const [wizardOpen, setWizardOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [apiPage, setApiPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Bulk Actions State
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false); // UI Clean-up

  // SMART AI: Real Implementation
  const [wizardStep, setWizardStep] = useState(1); // 1: Image, 2: Core, 3: Enhance
  const [aiStatus, setAiStatus] = useState('idle'); // idle, scanning, complete

  // Robust Close/Reset
  const handleCloseWizard = () => {
    setAddModalOpen(false);
    setAddForm(initialAddState); // Reset form
    setWizardStep(1); // Back to Step 1
    setPendingFile(null);
    setTempImgSrc(null);
    setAiStatus('idle');
  };

  const handleAutoFill = async (overrideImage = null) => {
    // Used directly after crop
    const imageToAnalyze = overrideImage || tempImgSrc;

    // For manual calls, grab from form if needed
    let finalImage = imageToAnalyze;
    if (!finalImage && addForm.image instanceof File) {
      try { finalImage = await convertBase64(addForm.image); } catch (e) { }
    }

    if (!finalImage) return; // Silent fail if no image in flow

    try {
      setAiStatus('scanning');
      // Call Backend API
      const response = await axios.post('/api/ai/analyze', { imageBase64: finalImage });
      const { success, data } = response.data;

      if (success && data) {
        // Auto-Magic Apply
        setAddForm(prev => ({
          ...prev,
          name: data.suggestedName || "Gold Item", // Fallback if AI empty
          type: data.type || "Gold",
          purity: (prev.purity && prev.purity !== '' && prev.purity.length > 2) ? prev.purity : (data.type === 'Gold' ? '22K' : '925'),
          description: data.suggestedDescription || "Handcrafted excellence.",
        }));
        setAiStatus('complete');
      } else {
        throw new Error("No Data");
      }
    } catch (error) {
      console.error("AI Silent Fail:", error);
      // Fallback for speed: Just generic fill so user has something
      setAddForm(prev => ({
        ...prev,
        name: "New Jewellery Item",
        type: "Gold",
        purity: "22K"
      }));
      setAiStatus('complete'); // Treat as complete even if fallback used
    }
  };

  // Step Navigation
  const goToStep2 = () => {
    // Validate Step 1
    if (!addForm.image) { alert("Please upload an image first"); return; }
    setWizardStep(2);
  };

  const handleSaveAndNext = async () => {
    const success = await handleAddSave(true); // true = keepOpen
    if (success) {
      // Reset for next item
      setAddForm({ ...initialAddState, image: '', visibility: false });
      setWizardStep(1); // Back to Step 1
      setPendingFile(null);
      setTempImgSrc(null);
      setAiStatus('idle');

      // Show micro-feedback
      const toast = document.createElement('div');
      toast.className = 'val-toast success';
      toast.innerText = '✨ Item Added! Ready for next.';
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 2500);
    }
  };

  const ITEMS_PER_PAGE = 24;

  const fetchJewellery = async (page = 1, append = false) => {
    setLoading(true);
    setError('');
    if (!append) setSelectedItems(new Set());

    try {
      const queryParams = { ...filters, isAdmin };
      if (isAdmin) delete queryParams.visibility;
      if (!isAdmin && !filters.showHidden) queryParams.visibility = true;
      if (isAdmin && filters.showHidden) queryParams.visibility = undefined;

      // Admin gets all items; public gets paginated
      if (!isAdmin) {
        queryParams.page = page;
        queryParams.limit = ITEMS_PER_PAGE;
      }

      const res = await axios.get(`${API_BASE_URL}/api/jewellery`, { params: queryParams });

      if (!isAdmin && res.data.items) {
        const { items: newItems, total, pages } = res.data;
        setItems(prev => append ? [...prev, ...newItems] : newItems);
        setTotalItems(total);
        setHasMore(page < pages);
        setApiPage(page);
      } else {
        setItems(Array.isArray(res.data) ? res.data : res.data.items || []);
        setTotalItems(0);
        setHasMore(false);
      }
    } catch (err) {
      setError('Failed to fetch jewellery items');
    }
    setLoading(false);
  };

  // SMART SUMMARY (Pro Dashboard)
  const summaryStats = React.useMemo(() => {
    if (!items.length) return { total: 0, value: 0, newItems: 0, drafts: 0 };

    let totalVal = 0;
    let newCount = 0;
    let draftCount = 0;

    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    items.forEach(item => {
      // Value
      const price = Number(item.price) || 0;
      totalVal += price;

      // Drafts
      if (item.status === 'Draft' || !item.visibility) draftCount++;

      // New items (using MongoDB _id timestamp as proxy if createdAt missing)
      // ObjectId: first 4 bytes are timestamp
      const timestamp = parseInt(item._id.substring(0, 8), 16) * 1000;
      const date = new Date(timestamp);
      if (date > oneWeekAgo) newCount++;
    });

    return {
      total: items.length,
      value: totalVal,
      newItems: newCount,
      drafts: draftCount
    };
  }, [items]);

  useEffect(() => {
    fetchJewellery(1, false);
    // eslint-disable-next-line
  }, [filters, isAdmin]);

  // SEO: Inject Product Schema
  useEffect(() => {
    if (!isAdmin && items.length > 0) {
      const schemaData = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "numberOfItems": items.length,
        "itemListElement": items.slice(0, 10).map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Product",
            "name": item.name,
            "image": getImageUrl(item.image),
            "description": item.description,
            "offers": {
              "@type": "Offer",
              "price": item.price,
              "priceCurrency": "INR",
              "availability": item.status === 'Sold' ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
            }
          }
        }))
      };
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schemaData);
      document.head.appendChild(script);
      return () => { if (script.parentNode) script.parentNode.removeChild(script); };
    }
  }, [items, isAdmin]);

  const openDetailModal = (item) => {
    setDetailItem(item);
  };

  // Edit logic
  const handleEditClick = (item) => {
    setEditItem(item);
    setEditForm({
      name: item.name,
      type: item.type,
      price: item.price,
      weight: item.weight,
      description: item.description || '',
      purity: item.purity || '',
      huid: item.huid || '',
      status: item.status || 'Available',
      visibility: item.visibility,
    });
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (name === 'image' && files && files.length > 0) {
      // Instead of setting directly, open cropper
      const file = files[0];
      setPendingFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setTempImgSrc(reader.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
      // Clear value so same file can be selected again if cancelled
      e.target.value = null;
    } else {
      const val = type === 'checkbox' ? checked : value;
      setEditForm(prev => ({ ...prev, [name]: val }));
    }
  };

  const handleEditSave = async () => {
    try {
      let base64Image = editForm.image;
      if (editForm.image instanceof File) {
        base64Image = await convertBase64(editForm.image);
      }

      const payload = { ...editForm, image: base64Image };

      await axios.put(`${API_BASE_URL}/api/jewellery/${editItem._id}`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      setEditItem(null);
      fetchJewellery(1, false);
    } catch (err) {
      console.error(err);
      alert('Failed to update item');
    }
  };

  // Toggle Visibility
  const toggleVisibility = async (item, e) => {
    e.stopPropagation();
    try {
      const newVisibility = !item.visibility;
      await axios.put(`${API_BASE_URL}/api/jewellery/${item._id}`, { visibility: newVisibility });
      setItems(prev => prev.map(i => i._id === item._id ? { ...i, visibility: newVisibility } : i));
    } catch (err) {
      alert('Failed to update visibility');
    }
  };

  // Delete logic
  const handleDelete = async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/jewellery/${id}`);
      fetchJewellery(1, false);
    } catch (err) {
      alert('Failed to delete item');
    }
  };

  const handleAddChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (name === 'image' && files && files.length > 0) {
      // Open cropper
      const file = files[0];
      setPendingFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setTempImgSrc(reader.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
      e.target.value = null;
    } else {
      const val = type === 'checkbox' ? checked : value;
      setAddForm(prev => ({ ...prev, [name]: val }));
    }
  };

  const handleAddSave = async (keepOpen = false) => {
    if (!addForm.name || !addForm.price || !addForm.weight) {
      alert("Please fill required fields (Name, Price, Weight)");
      return false;
    }
    try {
      let base64Image = addForm.image;
      if (addForm.image instanceof File) {
        base64Image = await convertBase64(addForm.image);
      }

      const payload = { ...addForm, image: base64Image };

      await axios.post(`${API_BASE_URL}/api/jewellery`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (!keepOpen) {
        setAddModalOpen(false);
        setAddForm(initialAddState);
        setWizardStep(1); // Reset wizard
      }
      fetchJewellery(1, false);
      return true;
    } catch (err) {
      alert('Failed to add item');
    }
  };

  const handleEnquireChange = (e) => {
    setEnquireForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleEnquireSubmit = async (e) => {
    e.preventDefault();
    try {
      // Mock enquiry
      setEnquireSuccess(true);
      setTimeout(() => {
        setEnquireOpen(false);
        setEnquireSuccess(false);
        setEnquireForm({ name: '', email: '', message: '' });
      }, 1500);
    } catch (err) {
      alert('Failed to send enquiry');
    }
  };

  // Bulk Selection Logic
  const handleSelectItem = (id, checked) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (checked) newSet.add(id);
      else newSet.delete(id);
      return newSet;
    });
  };

  const handleCropComplete = async (croppedBlob) => {
    // Reconstruct a File object from the blob to keep logic consistent
    // Use original name but ensure .webp extension
    const originalName = pendingFile ? pendingFile.name : 'image.webp';
    const fileName = originalName.substr(0, originalName.lastIndexOf('.')) + ".webp";
    const croppedFile = new File([croppedBlob], fileName, { type: "image/webp" });

    // Generate Base64 for Preview & AI immediately
    const reader = new FileReader();
    reader.readAsDataURL(croppedBlob);
    reader.onloadend = () => {
      const base64data = reader.result;

      if (editItem) {
        setEditForm(prev => ({ ...prev, image: croppedFile }));
      } else {
        setAddForm(prev => ({ ...prev, image: croppedFile }));
        // AUTO-MAGIC: Move to Step 2 & Trigger AI
        setWizardStep(2);
        // Use timeout to allow render update or just fire
        handleAutoFill(base64data);
      }
    };

    setShowCropper(false);
    // tempImgSrc is purposely NOT cleared yet so AI can use it as fallback if needed, 
    // but we passed it explicitly. We can clear it or leave it.
    // setTempImgSrc(null); 
    setPendingFile(null);

    // validateImage(croppedFile); // Skip val, we know it's good from cropper
  };


  const handleCropCancel = () => {
    setShowCropper(false);
    setTempImgSrc(null);
    setPendingFile(null);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = items.map(i => i._id);
      setSelectedItems(new Set(allIds));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleBulkAction = async (action, value = null) => {
    if (selectedItems.size === 0) return;
    if (!window.confirm(`Apply '${action}' to ${selectedItems.size} items?`)) return;

    setIsBulkProcessing(true);
    try {
      let updateData = {};
      if (action === 'delete') {
        // managed by action param
      } else if (action === 'hide') {
        updateData = { visibility: false };
      } else if (action === 'show') {
        updateData = { visibility: true };
      } else if (action === 'mark_sold') {
        updateData = { status: 'Sold' };
      } else if (action === 'mark_available') {
        updateData = { status: 'Available' };
      }

      const payload = {
        ids: Array.from(selectedItems),
        action: action === 'delete' ? 'delete' : 'update',
        updateData
      };

      await axios.post('/api/jewellery/bulk', payload);

      // Refresh and clear selection
      fetchJewellery(1, false);
      alert('Bulk action completed successfully');

    } catch (err) {
      console.error(err);
      alert('Bulk action failed');
    }
    setIsBulkProcessing(false);
  };

  // Helper to generate SEO slug
  const getProductSlug = (item) => {
    if (!item || !item.name) return item?._id;

    // Basic slugify: lowercase, replace spaces/special chars with hyphens
    const basicSlug = item.name.toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');

    // Check for collisions in the current ITEMS list
    // Note: This relies on 'items' being populated. 
    // In a real large-scale app, we'd store the slug in DB. 
    // For now, client-side calculation is fine.
    const isDuplicate = items.filter(i => {
      const otherSlug = i.name.toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
      return otherSlug === basicSlug;
    }).length > 1;

    if (isDuplicate && item.weight) {
      return `${basicSlug}-${Math.floor(item.weight)}g`;
    }

    return basicSlug;
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'gold':
        return (
          <svg className="type-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
        );
      case 'silver':
        return (
          <svg className="type-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
          </svg>
        );
      default:
        return (
          <svg className="type-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        );
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Status Badge Helper
  const getStatusBadge = (item) => {
    if (item.status === 'Draft') return <span className="status-badge status-draft">Draft</span>;
    if (!item.visibility) return <span className="status-badge status-hidden">Hidden</span>;
    return null; // Active & Visible don't need a badge on card per se, or maybe "Live"
  };

  // Helper for Similar Items
  const getSimilarItems = (currentItem) => {
    if (!currentItem) return [];
    return items.filter(i =>
      i._id !== currentItem._id &&
      (i.type === currentItem.type || i.category === currentItem.category) &&
      Math.abs(i.price - currentItem.price) < (currentItem.price * 0.3) // +/- 30% price range
    ).slice(0, 4);
  };

  const handleExportCSV = () => {
    if (!items.length) {
      alert("No items to export");
      return;
    }

    // Define CSV headers
    const headers = ["ID", "Name", "Type", "Category", "Price", "Weight", "Purity", "Status", "Visibility", "Description"];

    // Map items to CSV rows
    const rows = items.map(item => [
      item._id,
      `"${item.name.replace(/"/g, '""')}"`, // Escape quotes
      item.type,
      item.category,
      item.price,
      item.weight,
      item.purity,
      item.status,
      item.visibility ? 'Visible' : 'Hidden',
      `"${(item.description || '').replace(/"/g, '""')}"`
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `jewellery_inventory_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper to get image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http') || imagePath.startsWith('data:')) return imagePath;

    // In production, backend serves frontend, so relative path works.
    // In dev, frontend is 3000, backend is 5050.
    const isProduction = process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost';
    const baseUrl = isProduction ? '' : 'http://localhost:5050';

    // Ensure path starts with /
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${baseUrl}${cleanPath.replace(/\\/g, '/')}`;
  };

  return (
    <div className="jewellery-page-modern">
      {/* Scroll Text Ticker */}
      <LiveRateTicker />

      {/* Hero Header - ONLY for Public Users */}
      {/* Hero Header Removed as per user request */}

      <HelpMeChooseWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onApplyFilters={(newFilters) => {
          setFilters(prev => ({ ...prev, ...newFilters }));
          setWizardOpen(false);
        }}
      />

      {/* Admin Compact Header with Sorting and Counts */}
      {isAdmin && (
        <div className="admin-header-compact">
          <div className="admin-header-info">
            <h2>Inventory Management</h2>
            {/* Simple count moved to Summary Bar */}
          </div>
          <div className="admin-header-actions">
            {/* Sorting Dropdown */}
            <div className="sort-box">
              <label>Sort by:</label>
              <select
                className="sort-select"
                value={filters.sortBy || 'createdAt'}
                onChange={(e) => {
                  const [sort, order] = e.target.value.split('_');
                  setFilters(prev => ({ ...prev, sortBy: sort, order: order || 'desc' }));
                }}
              >
                <option value="createdAt_desc">Latest Added</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="weight_desc">Weight: High to Low</option>
              </select>
            </div>

            <div className="tooltip-container">
              <button className="btn-secondary btn-export" onClick={handleExportCSV}>Export CSV</button>
              <span className="tooltip-text">Exports currently filtered items</span>
            </div>
            <button className="btn-primary btn-add" onClick={() => setAddModalOpen(true)}>+ Add New Item</button>
          </div>
        </div>
      )}

      {/* NEW: PRO DASHBOARD SUMMARY BAR */}
      {isAdmin && (
        <div className="admin-summary-bar fade-in">
          <div className="summary-card">
            <span className="sc-label">Total Designs</span>
            <span className="sc-value">{summaryStats.total}</span>
          </div>
          <div className="summary-card">
            <span className="sc-label">Inventory Value</span>
            <span className="sc-value">
              {new Intl.NumberFormat('en-IN', {
                style: 'currency', currency: 'INR', maximumFractionDigits: 0
              }).format(summaryStats.value)}
            </span>
          </div>
          <div className="summary-card highlight">
            <span className="sc-label">Added This Week</span>
            <span className="sc-value">+{summaryStats.newItems}</span>
          </div>
          <div className="summary-card warning">
            <span className="sc-label">Drafts / Hidden</span>
            <span className="sc-value">{summaryStats.drafts}</span>
          </div>
        </div>
      )}

      <div className="jewellery-content-container" style={{ marginTop: isAdmin ? '20px' : '0' }}>
        {/* Mobile Filter Toggle */}
        <div className="mobile-filter-toggle">
          <button className="btn-filter-toggle" onClick={() => setShowFilters(!showFilters)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
            {showFilters ? 'Hide Filters' : 'Filters'}
          </button>
        </div>

        {/* Modern Filters */}
        <aside className={`filters-sidebar ${showFilters ? 'active' : ''}`}>
          <div className="sidebar-header-mobile">
            <h3>Filters</h3>
            <button className="btn-close-sidebar" onClick={() => setShowFilters(false)}>×</button>
          </div>
          <JewelleryFilterBar filters={filters} setFilters={setFilters} isAdmin={isAdmin} />
        </aside>

        {/* Main Grid */}
        <main className="jewellery-main">
          {/* View Toggle & Count (Mobile friendly) */}
          <div className="view-controls">
            <div className="view-controls-left">
              <span className="item-count-badge">{isAdmin ? items.length : (totalItems || items.length)} Designs</span>

              {/* Erase.io style Help Me Choose Button */}
              {!isAdmin && (
                <button className="btn-erase-ai" onClick={() => setWizardOpen(true)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                  </svg>
                  Help Me Choose
                </button>
              )}
            </div>

            <div className="view-toggles">
              <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} title="Grid View">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M4 11h5V5H4v6zm0 7h5v-6H4v6zm6 0h5v-6h-5v6zm6 0h5v-6h-5v6zm-6-7h5V5h-5v6zm6-6v6h5V5h-5z" /></svg>
              </button>
              <button className={`view-btn ${viewMode === 'lookbook' ? 'active' : ''}`} onClick={() => setViewMode('lookbook')} title="Lookbook View">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M4 18h17v-6H4v6zM4 5v6h17V5H4z" /></svg>
              </button>
            </div>
          </div>
          {loading && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
            </div>
          )}

          {error && (
            <div className="error-container">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && items.length === 0 && (
            <div className="no-results-modern">
              {/* Improved Empty State */}
              <div className="no-results-icon">📦</div>
              <h3>{isAdmin ? 'No inventory items yet' : 'Collection Coming Soon'}</h3>
              <p>{isAdmin ? 'Add your first item to get started.' : 'Stay tuned for our exclusive collection.'}</p>
              {isAdmin && (
                <button className="btn-primary" onClick={() => setAddModalOpen(true)}>Add Item Now</button>
              )}
            </div>
          )}

          {/* Bulk Action Bar - Sticky Bottom */}
          {isAdmin && selectedItems.size > 0 && (
            <div className="bulk-action-bar">
              <div className="bulk-info">
                <span className="selected-count">{selectedItems.size} Selected</span>
                <button className="btn-bulk-text" onClick={() => handleSelectAll(false)}>Clear</button>
              </div>
              <div className="bulk-actions">
                <button className="btn-bulk btn-bulk-sold" onClick={() => handleBulkAction('mark_sold')}>Mark Sold</button>
                <button className="btn-bulk btn-bulk-hide" onClick={() => handleBulkAction('hide')}>Hide</button>
                <button className="btn-bulk btn-bulk-show" onClick={() => handleBulkAction('show')}>Show</button>
                {user?.role !== 'co-admin' && (
                  <button className="btn-bulk btn-bulk-delete" onClick={() => handleBulkAction('delete')}>Delete</button>
                )}
              </div>
            </div>
          )}

          <div className="admin-controls-header">
            {isAdmin && items.length > 0 && (
              <div className="admin-select-all">
                <label>
                  <input
                    type="checkbox"
                    checked={selectedItems.size === items.length && items.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                  Select All ({items.length})
                </label>
              </div>
            )}
          </div>

          <div className={`jewellery-grid-modern ${viewMode === 'lookbook' ? 'lookbook-mode' : ''}`}>
            {items.map((item, idx) => (
              <ProductCard
                key={item._id}
                item={item}
                index={idx}
                isAdmin={isAdmin}
                onEdit={handleEditClick}
                onDelete={handleDelete}
                onToggleVisibility={toggleVisibility}
                onView={(item) => isAdmin ? setDetailItem(item) : navigate(`/jewellery/${getProductSlug(item)}`)}
                isSelected={selectedItems.has(item._id)}
                onSelect={(checked) => handleSelectItem(item._id, checked)}
              />
            ))}
          </div>

          {hasMore && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <button
                className="btn-secondary"
                onClick={() => fetchJewellery(apiPage + 1, true)}
                disabled={loading}
                style={{ padding: '12px 40px', fontSize: '15px' }}
              >
                {loading ? 'Loading...' : `Load More (${totalItems - items.length} remaining)`}
              </button>
            </div>
          )}
        </main>
      </div>

      {/* Modern Detail Modal */}
      {detailItem && (
        <div className="modal-overlay-modern" onClick={() => setDetailItem(null)}>
          <div className="modal-content-modern" onClick={e => e.stopPropagation()}>
            <button className="btn-close-modal" onClick={() => setDetailItem(null)}>×</button>

            <div className="modal-split">
              <div className="modal-image-col">
                <div
                  className="image-zoom-container loading-skeleton"
                  style={{ width: '100%', overflow: 'hidden', cursor: 'crosshair' }}
                  onMouseMove={(e) => {
                    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
                    const x = ((e.clientX - left) / width) * 100;
                    const y = ((e.clientY - top) / height) * 100;
                    e.currentTarget.style.setProperty('--x', `${x}%`);
                    e.currentTarget.style.setProperty('--y', `${y}%`);
                  }}
                >
                  {detailItem.image ? (
                    <>
                      <img
                        src={getImageUrl(detailItem.image)}
                        alt={detailItem.name}
                        className="detail-main-image product-img"
                        onLoad={(e) => {
                          e.target.classList.add('loaded');
                          e.target.parentElement.classList.remove('loading-skeleton');
                        }}
                        onError={(e) => {
                          e.target.parentElement.classList.remove('loading-skeleton');
                        }}
                      />
                      <div className="zoom-hint">Hover to Zoom</div>
                    </>
                  ) : (
                    <div className="modal-placeholder-large">{getTypeIcon(detailItem.type)}</div>
                  )}
                </div>
              </div>

              <div className="modal-info-col">
                <span className="modal-category">{detailItem.type} Jewellery</span>
                <h2>{detailItem.name}</h2>
                <div className="modal-price">{formatPrice(detailItem.price)}</div>

                <div className="spec-item">
                  <span className="spec-label">Weight</span>
                  <span className="spec-value">{detailItem.weight}g</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Purity</span>
                  <span className="spec-value">{detailItem.purity || 'N/A'}</span>
                </div>
                {detailItem.huid && (
                  <div className="spec-item">
                    <span className="spec-label">HUID</span>
                    <span className="spec-value highlight-gold">{detailItem.huid}</span>
                  </div>
                )}

                {/* Visual Storytelling: Karigar (Admin Only) */}
                {isAdmin && detailItem.karigar && (
                  <div className="spec-item full-width-spec">
                    <span className="spec-label">Craftsmanship</span>
                    <span className="spec-value highlight-gold">Handcrafted by Master Artisan {detailItem.karigar}</span>
                  </div>
                )}

                <div className="spec-item">
                  <span className="spec-label">Status</span>
                  <span className="spec-value">{detailItem.status || 'Available'}</span>
                </div>


                {/* Trust Badges in Modal */}
                <div className="modal-trust-badges">
                  <span className="trust-badge-small">🏆 BIS Hallmarked</span>
                  <span className="trust-badge-small">💎 Certified Pure</span>
                  <span className="trust-badge-small">✨ Lifetime Shine</span>
                </div>

                <p className="modal-description">{detailItem.description || 'No description available for this exclusive piece.'}</p>

                {/* Admin Actions in Modal */}
                {isAdmin && (
                  <div className="modal-admin-actions">
                    <button className="btn-secondary" onClick={() => { setEditItem(detailItem); setDetailItem(null); }}>Edit Item</button>
                    <button className="btn-secondary" onClick={() => setDetailItem(null)}>Close</button>
                  </div>
                )}

                {/* Similar Items in Modal - Conditional (Only if exists) */}
                {getSimilarItems(detailItem).length > 0 && (
                  <div className="modal-similar-section">
                    <h3>You May Also Like</h3>
                    <div className="similar-grid">
                      {getSimilarItems(detailItem).slice(0, 4).map(sim => (
                        <div className="similar-card" key={sim._id} onClick={() => setDetailItem(sim)}>
                          {sim.image ? <img src={getImageUrl(sim.image)} alt={sim.name} /> : <div className="sim-placeholder">{getTypeIcon(sim.type)}</div>}
                          <div className="sim-info">
                            <h4>{sim.name}</h4>
                            <span>{formatPrice(sim.price)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="modal-actions">
                  <div className="public-actions-row">
                    <button className="btn-whatsapp" onClick={() => {
                      const message = `Hello, I am interested in *${detailItem.name}*.\n\n*Details:*\nPrice: ₹${detailItem.price}\nWeight: ${detailItem.weight}g\nPurity: ${detailItem.purity || '22K'}\n\nPlease share more details.`;
                      const url = `https://wa.me/919414817620?text=${encodeURIComponent(message)}`;
                      window.open(url, '_blank');
                    }}>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" /></svg>
                      Get on WhatsApp
                    </button>
                  </div>
                </div>
              </div>

              {/* Similar Items Section in Modal */}
              <div className="modal-similar-section">
                <h3>You May Also Like</h3>
                <div className="similar-grid">
                  {getSimilarItems(detailItem).map(sim => (
                    <div key={sim._id} className="similar-card" onClick={(e) => { e.stopPropagation(); setDetailItem(sim); }}>
                      <div className="similar-img-box">
                        {sim.image ? <img src={getImageUrl(sim.image)} alt={sim.name} /> : <div className="placeholder-sm">{getTypeIcon(sim.type)}</div>}
                      </div>
                      <div className="similar-info">
                        <h4>{sim.name}</h4>
                        <span>{formatPrice(sim.price)}</span>
                      </div>
                    </div>
                  ))}
                  {getSimilarItems(detailItem).length === 0 && <p style={{ color: '#888', fontSize: '0.9rem' }}>No similar items found right now.</p>}
                </div>
              </div>

            </div>
          </div>

          {/* Nested Enquire Modal - Kept Simple */}
          {enquireOpen && (
            <div className="enquire-overlay">
              <div className="enquire-box">
                <h3>Enquire about {detailItem.name}</h3>
                {enquireSuccess ? (
                  <div className="success-msg">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
                    <p>Enquiry sent successfully!</p>
                  </div>
                ) : (
                  <form onSubmit={handleEnquireSubmit}>
                    <input name="name" value={enquireForm.name} onChange={handleEnquireChange} placeholder="Your Name" required />
                    <input name="email" value={enquireForm.email} onChange={handleEnquireChange} placeholder="Your Email" type="email" required />
                    <textarea name="message" value={enquireForm.message} onChange={handleEnquireChange} placeholder="Message" rows={3} required />
                    <div className="form-actions">
                      <button type="button" className="btn-cancel" onClick={() => setEnquireOpen(false)}>Cancel</button>
                      <button type="submit" className="btn-submit">Send</button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit/Add Modals (Enhanced fields & Smart AI Tagging) */}
      {(editItem || addModalOpen) && (
        <div className="modal-overlay-modern">
          <div className="modal-form-modern wizard-modal">

            {/* Header / Progress */}
            <div className="wizard-header">
              <h3>{editItem ? 'Edit Item' : 'Add New Item'}</h3>
              {!editItem && (
                <div className="wizard-steps-indicator">
                  <span className={`step-dot ${wizardStep >= 1 ? 'active' : ''}`}>1</span>
                  <span className="step-line"></span>
                  <span className={`step-dot ${wizardStep >= 2 ? 'active' : ''}`}>2</span>
                  <span className="step-line"></span>
                  <span className={`step-dot ${wizardStep >= 3 ? 'active' : ''}`}>3</span>
                </div>
              )}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); editItem ? handleEditSave() : handleAddSave(); }} encType="multipart/form-data">

              {/* STEP 1: IMAGE (Mandatory) */}
              {(wizardStep === 1 && !editItem) && (
                <div className="wizard-step step-1 fade-in">
                  <div className="big-dropzone">
                    <input type="file" id="file-upload" name="image" onChange={handleAddChange} className="file-input-hidden" />
                    <label htmlFor="file-upload" className="dropzone-label">
                      <div className="icon-cloud-upload">☁️</div>
                      <h3>Upload Item Image</h3>
                      <p>Drag & drop or Click to Browse</p>
                      <span className="sub-text">1:1 Ratio • Auto-Cropped • WebP Optimized</span>
                    </label>
                  </div>
                  {/* AI Pulse Indicator */}
                  {aiStatus === 'scanning' && (
                    <div id="ai-pulse-indicator" className="ai-pulse">
                      <span>🤖 AI Analyzing...</span>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 2: CORE DETAILS (Fast Entry) */}
              {(wizardStep === 2 || editItem) && (
                <div className="wizard-step step-2 fade-in">

                  {/* Image Preview Mini (if just uploaded) */}
                  {!editItem && addForm.image && (
                    <div className="mini-preview-row">
                      <span className="success-pill">✓ Image Ready</span>
                      {aiStatus === 'complete' && <span className="magic-pill">✨ AI Auto-Filled</span>}
                    </div>
                  )}

                  <div className="form-group-modern">
                    <label>Item Name (AI Suggested)</label>
                    <input name="name" value={editItem ? editForm.name : addForm.name} onChange={editItem ? handleEditChange : handleAddChange} placeholder="e.g. Royal Gold Necklace" required className="modern-input large-input" autoFocus />
                  </div>

                  <div className="form-grid-2">
                    <div className="form-group-modern">
                      <label>Type</label>
                      <div className="visual-options">
                        {['Gold', 'Silver', 'Diamond', 'Custom'].map(t => (
                          <button type="button" key={t}
                            className={`vis-btn ${(editItem ? editForm.type : addForm.type) === t ? 'selected' : ''}`}
                            onClick={() => {
                              const e = { target: { name: 'type', value: t } };
                              editItem ? handleEditChange(e) : handleAddChange(e);
                            }}>
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="form-group-modern">
                      <label>Purity</label>
                      <div className="visual-options">
                        {['22K', '18K', '925', 'Custom'].map(p => (
                          <button type="button" key={p}
                            className={`vis-btn ${(editItem ? editForm.purity : addForm.purity) === p ? 'selected' : ''}`}
                            onClick={() => {
                              const e = { target: { name: 'purity', value: p } };
                              editItem ? handleEditChange(e) : handleAddChange(e);
                            }}>
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="form-grid-2">
                    <div className="form-group-modern">
                      <label>Weight (g)</label>
                      <input name="weight" type="number" value={editItem ? editForm.weight : addForm.weight} onChange={editItem ? handleEditChange : handleAddChange} placeholder="0.00" required className="modern-input" />
                    </div>
                    <div className="form-group-modern">
                      <label>Price (₹)</label>
                      <input name="price" type="number" value={editItem ? editForm.price : addForm.price} onChange={editItem ? handleEditChange : handleAddChange} placeholder="0" required className="modern-input" />
                    </div>
                  </div>

                  {/* Default Visibility Toggle */}
                  <div className="form-group-modern toggle-row">
                    <label>Status: </label>
                    <div className="toggle-switch">
                      <button type="button" className={`tog-btn ${(editItem ? editForm.status : addForm.status) === 'Draft' ? 'active' : ''}`} onClick={(e) => { const ev = { target: { name: 'status', value: 'Draft' } }; editItem ? handleEditChange(ev) : handleAddChange(ev); }}>Draft</button>
                      <button type="button" className={`tog-btn ${(editItem ? editForm.status : addForm.status) === 'Available' ? 'active' : ''}`} onClick={(e) => { const ev = { target: { name: 'status', value: 'Available' } }; editItem ? handleEditChange(ev) : handleAddChange(ev); }}>Live</button>
                    </div>
                  </div>

                  {/* Link to Step 3 */}
                  <div className="step-link" onClick={() => setWizardStep(3)}>
                    <span>Add More Details (HUID, Story...) ▼</span>
                  </div>
                </div>
              )}

              {/* STEP 3: ENHANCE (Optional) */}
              {(wizardStep === 3) && (
                <div className="wizard-step step-3 fade-in">
                  <div className="form-group-modern">
                    <label>Description & Story</label>
                    <textarea name="description" value={editItem ? editForm.description : addForm.description} onChange={editItem ? handleEditChange : handleAddChange} placeholder="Tell the story of this piece..." rows={3} className="modern-input" />
                  </div>
                  <div className="form-group-modern">
                    <label>HUID (Traceability)</label>
                    <input name="huid" value={editItem ? editForm.huid : addForm.huid} onChange={editItem ? handleEditChange : handleAddChange} placeholder="XXXXXX" className="modern-input" />
                  </div>

                  <div className="step-link" onClick={() => setWizardStep(2)}>
                    <span>▲ Back to Core Details</span>
                  </div>
                </div>
              )}

              <div className="wizard-actions">
                {wizardStep === 1 && !editItem ? (
                  <button type="button" className="btn-cancel" onClick={handleCloseWizard}>Cancel</button>
                ) : (
                  <>
                    <button type="button" className="btn-secondary" onClick={() => editItem ? setEditItem(null) : handleCloseWizard()}>Close</button>

                    {!editItem ? (
                      <>
                        <button type="submit" className="btn-primary">Save & Close</button>
                        <button type="button" className="btn-cta-gold" onClick={handleSaveAndNext}>Save & Add Next ⭐</button>
                      </>
                    ) : (
                      <button type="submit" className="btn-primary">Update Item</button>
                    )}
                  </>
                )}
              </div>

            </form>
          </div>
        </div>
      )}

      {showCropper && tempImgSrc && (
        <ImageCropper
          imageSrc={tempImgSrc}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
};

export default JewelleryListPage;